import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Aperture, Link2, Loader2, Share2 } from "lucide-react";
import { toast } from "sonner";
import { api, API } from "@/lib/api";

const fmtDate = (iso) =>
  new Date(iso).toLocaleDateString("de-DE", { day: "numeric", month: "long", year: "numeric" });

const shareMoment = async (m) => {
  const url = `${window.location.origin}/momente#m-${m.id}`;
  const text = m.caption ? `„${m.caption}" — ein Moment von Leno Fotografie` : "Ein Moment von Leno Fotografie";
  if (navigator.share) {
    try {
      await navigator.share({ title: "Leno Fotografie — Moment", text, url });
      return;
    } catch {
      return; // abgebrochen
    }
  }
  window.open(`https://wa.me/?text=${encodeURIComponent(`${text} ${url}`)}`, "_blank", "noopener");
};

const copyLink = async (m) => {
  const url = `${window.location.origin}/momente#m-${m.id}`;
  try {
    await navigator.clipboard.writeText(url);
    toast.success("Link kopiert — bereit zum Teilen.");
  } catch {
    toast.error("Kopieren nicht möglich.");
  }
};

export default function Momente() {
  const [moments, setMoments] = useState(null);

  useEffect(() => {
    api
      .get("/moments")
      .then((res) => setMoments(res.data))
      .catch(() => setMoments([]));
  }, []);

  return (
    <div className="min-h-screen bg-ink text-white" data-testid="momente-page">
      <header className="border-b border-white/10">
        <div className="mx-auto flex max-w-[1200px] items-center justify-between px-6 py-5">
          <Link to="/" className="flex items-center gap-3" data-testid="momente-logo-link">
            <img src="/assets/logo-mark.png" alt="Leno Fotografie" className="h-9 w-9 rounded-full object-cover ring-1 ring-white/15" />
            <span className="font-sans text-lg font-semibold tracking-[0.18em]">LENO</span>
          </Link>
          <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold">Momente — frisch aus der Kamera</span>
        </div>
      </header>

      <main className="mx-auto max-w-[820px] px-6 py-16">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} className="text-center">
          <p className="font-mono text-[10px] uppercase tracking-[0.35em] text-gold">Immer aktuell</p>
          <h1 className="mt-4 font-serif text-4xl font-light tracking-tight sm:text-5xl" data-testid="momente-title">
            Kleine Momente, <span className="italic text-zinc-400">große Gefühle.</span>
          </h1>
          <p className="mx-auto mt-4 max-w-md text-sm leading-relaxed text-zinc-400">
            Meine neuesten Aufnahmen — direkt vom Shooting, ohne langes Warten.
          </p>
        </motion.div>

        {moments === null ? (
          <div className="flex justify-center py-24" data-testid="momente-loading">
            <Loader2 size={22} className="animate-spin text-gold" />
          </div>
        ) : moments.length === 0 ? (
          <div className="mt-16 border border-white/10 bg-ink2 p-16 text-center" data-testid="momente-empty">
            <Aperture size={26} className="mx-auto text-gold" />
            <p className="mt-5 font-serif text-xl text-zinc-300">Hier entstehen bald frische Momente.</p>
            <p className="mt-2 text-sm text-zinc-500">Schau in ein paar Tagen wieder vorbei.</p>
          </div>
        ) : (
          <div className="mt-16 space-y-20">
            {moments.map((m, i) => (
              <motion.article
                key={m.id}
                id={`m-${m.id}`}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                className={i % 2 === 1 ? "sm:ml-16" : "sm:mr-16"}
                data-testid={`moment-${m.id}`}
              >
                <div className="group relative overflow-hidden border border-white/10 bg-ink2" data-cursor="view">
                  <img
                    src={`${API}/files/${m.storage_path}`}
                    alt={m.caption || "Moment"}
                    loading="lazy"
                    decoding="async"
                    className="w-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
                  />
                  <div className="absolute right-3 top-3 flex gap-2 transition-opacity duration-300 sm:opacity-0 sm:group-hover:opacity-100">
                    <button
                      onClick={() => shareMoment(m)}
                      data-testid={`moment-share-${m.id}`}
                      aria-label="Moment teilen"
                      className="flex h-10 w-10 items-center justify-center rounded-full bg-ink/80 text-gold backdrop-blur transition-colors hover:bg-gold hover:text-ink"
                    >
                      <Share2 size={15} />
                    </button>
                    <button
                      onClick={() => copyLink(m)}
                      data-testid={`moment-copy-${m.id}`}
                      aria-label="Link kopieren"
                      className="flex h-10 w-10 items-center justify-center rounded-full bg-ink/80 text-gold backdrop-blur transition-colors hover:bg-gold hover:text-ink"
                    >
                      <Link2 size={15} />
                    </button>
                  </div>
                </div>
                <div className="mt-4 flex items-baseline justify-between gap-4">
                  {m.caption ? (
                    <p className="font-serif text-lg italic leading-relaxed text-zinc-200">{m.caption}</p>
                  ) : (
                    <span />
                  )}
                  <p className="shrink-0 font-mono text-[9px] uppercase tracking-[0.25em] text-zinc-600">
                    {fmtDate(m.created_at)}
                  </p>
                </div>
              </motion.article>
            ))}
          </div>
        )}

        <p className="mt-20 border-t border-white/10 pt-8 text-center font-mono text-[9px] uppercase tracking-[0.25em] text-zinc-600">
          Mit Liebe gemacht — Leno Fotografie · lenofotografie.website
        </p>
      </main>
    </div>
  );
}
